import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const EMPTY = {
  nome: "", codice_commessa: "", cliente: "", indirizzo: "", budget_stimato: "",
  data_inizio: "", data_fine_presunta: "", stato: "attivo", sal_percentuale: 0, direttore_cantiere_id: "", note: "",
};

export default function CantiereModal({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [direttori, setDirettori] = useState([]);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial, direttore_cantiere_id: initial.direttore_cantiere_id || "", budget_stimato: String(initial.budget_stimato ?? "") } : EMPTY);
      api.get("/direttori").then(setDirettori).catch(() => {});
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nome.trim()) return toast.error("Inserisci il nome del cantiere.");
    const payload = {
      nome: form.nome.trim(),
      codice_commessa: form.codice_commessa?.trim() || "",
      cliente: form.cliente?.trim() || "",
      indirizzo: form.indirizzo?.trim() || "",
      budget_stimato: Number(form.budget_stimato) || 0,
      data_inizio: form.data_inizio || null,
      data_fine_presunta: form.data_fine_presunta || null,
      stato: form.stato,
      sal_percentuale: Number(form.sal_percentuale) || 0,
      direttore_cantiere_id: form.direttore_cantiere_id || null,
      note: form.note?.trim() || "",
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/cantieri/${initial.id}`, payload);
        toast.success("Cantiere aggiornato con successo.");
      } else {
        await api.post("/cantieri", payload);
        toast.success("Cantiere creato con successo.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="cantiere-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica Cantiere" : "Nuovo Cantiere"}</DialogTitle>
          <DialogDescription>Inserisci i dati del cantiere / commessa e lo stato avanzamento lavori.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Nome Cantiere</Label>
            <Input data-testid="cantiere-nome-input" placeholder="Es. Ristrutturazione Villa Rossi"
              value={form.nome} onChange={(e) => set("nome", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Codice Commessa</Label>
              <Input data-testid="cantiere-codice-input" placeholder="COM-2026-01"
                value={form.codice_commessa} onChange={(e) => set("codice_commessa", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Cliente</Label>
              <Input data-testid="cantiere-cliente-input" placeholder="Fam. Rossi"
                value={form.cliente} onChange={(e) => set("cliente", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Indirizzo</Label>
            <Input data-testid="cantiere-indirizzo-input" placeholder="Via delle Rose 12, Milano"
              value={form.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Budget Stimato (€)</Label>
              <Input type="number" step="0.01" min="0" data-testid="cantiere-budget-input" placeholder="0,00"
                value={form.budget_stimato} onChange={(e) => set("budget_stimato", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
                <SelectTrigger data-testid="cantiere-stato-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="in_pausa">In Pausa</SelectItem>
                  <SelectItem value="completato">Completato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Data Inizio</Label>
              <Input type="date" data-testid="cantiere-inizio-input" value={form.data_inizio || ""} onChange={(e) => set("data_inizio", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Fine Presunta</Label>
              <Input type="date" data-testid="cantiere-fine-input" value={form.data_fine_presunta || ""} onChange={(e) => set("data_fine_presunta", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Direttore di cantiere</Label>
            <Select value={form.direttore_cantiere_id || "none"} onValueChange={(v) => set("direttore_cantiere_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="cantiere-direttore-select"><SelectValue placeholder="Seleziona direttore..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessuno</SelectItem>
                {direttori.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">La sua firma verrà usata nei PDF ODA/CEL firmati. Gestisci i direttori in Archivio.</p>
          </div>
          <div className="space-y-2">
            <Label>Stato Avanzamento Lavori (SAL): {form.sal_percentuale}%</Label>
            <Slider data-testid="cantiere-sal-slider" value={[Number(form.sal_percentuale)]} min={0} max={100} step={5}
              onValueChange={(v) => set("sal_percentuale", v[0])} />
          </div>
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Textarea rows={2} data-testid="cantiere-note-input" value={form.note} onChange={(e) => set("note", e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cantiere-cancel-button">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="cantiere-save-button"
            className="bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
